import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Save, Settings } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { EmptyState, Panel } from "@/components/admin/ui";

const ORG_STATUSES = ["Active", "Onboarding", "Paused", "Suspended", "Inactive"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PLATFORM_EMAIL_FIELDS = [
  { key: "welcome_email", label: "Welcome email", hint: "Sent to new users when their workspace is set up." },
  { key: "support_email", label: "Support email", hint: "Reserved for future support workflows." },
  { key: "demo_request_email", label: "Demo request email", hint: "Used for demo request confirmations and admin notifications." },
  { key: "quotes_email", label: "Quotes email", hint: "Used when sending quotes and quote-accepted notifications." },
  { key: "billing_email", label: "Billing email", hint: "Used for billing and payment communications." },
  { key: "access_email", label: "Access email", hint: "Shown to organizations when dashboard access is unavailable." },
  { key: "integration_requests_email", label: "Integration requests email", hint: "Shown on the public integrations page." },
];

const Toggle = ({ checked, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className={`relative h-7 w-12 shrink-0 rounded-full p-0 transition-colors duration-200 ${checked ? "bg-[#0052FF]" : "bg-slate-300 dark:bg-white/15"}`}
  >
    <span className={`absolute left-0 top-1 h-5 w-5 rounded-full bg-white transition-transform duration-200 ${checked ? "translate-x-6" : "translate-x-1"}`} />
  </button>
);

export default function AdminSettings() {
  const { API } = useAuth();
  const [settings, setSettings] = useState(null);
  const [demoRecipients, setDemoRecipients] = useState("");
  const [quoteRecipients, setQuoteRecipients] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [emailErrors, setEmailErrors] = useState({});

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/platform-settings`, { withCredentials: true });
      setSettings(data.settings);
      setDemoRecipients((data.settings.demo_request_notifications?.recipients || []).join(", "));
      setQuoteRecipients((data.settings.quote_accepted_notifications?.recipients || []).join(", "));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => setSettings((current) => ({ ...current, [key]: value }));
  const updateEmail = (key, value) => {
    setSettings((current) => ({ ...current, platform_emails: { ...current.platform_emails, [key]: value } }));
    setEmailErrors((current) => ({ ...current, [key]: false }));
  };

  const save = async () => {
    setMessage("");
    setError("");

    const invalid = {};
    for (const field of PLATFORM_EMAIL_FIELDS) {
      const value = (settings.platform_emails?.[field.key] || "").trim();
      if (!value || !EMAIL_RE.test(value)) invalid[field.key] = true;
    }
    if (Object.keys(invalid).length > 0) {
      setEmailErrors(invalid);
      setError("Please enter a valid, non-blank email address for every platform email field.");
      return;
    }

    const limitFields = [
      ["default_max_org_admins", 0],
      ["default_max_teams", 0],
      ["default_max_users_per_team", 1],
      ["default_max_nova_billing_users", 0],
    ];
    for (const [key, min] of limitFields) {
      const value = settings[key];
      if (value === "" || value === null || value === undefined || Number.isNaN(Number(value)) || Number(value) < min) {
        setError("Please enter a valid, non-negative number for every Organization Access Limit field.");
        return;
      }
    }

    const inactivityMinutes = Number(settings.inactivity_timeout_minutes);
    if (Number.isNaN(inactivityMinutes) || inactivityMinutes < 30 || inactivityMinutes > 100) {
      setError("Session inactivity timeout must be between 30 and 100 minutes.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        platform_name: settings.platform_name,
        platform_emails: settings.platform_emails,
        demo_request_notifications: {
          enabled: settings.demo_request_notifications?.enabled || false,
          recipients: demoRecipients.split(",").map((item) => item.trim()).filter(Boolean),
        },
        quote_accepted_notifications: {
          enabled: settings.quote_accepted_notifications?.enabled || false,
          recipients: quoteRecipients.split(",").map((item) => item.trim()).filter(Boolean),
        },
        invitation_expiration_days: Number(settings.invitation_expiration_days),
        default_organization_status: settings.default_organization_status,
        solutions_globally_enabled: settings.solutions_globally_enabled,
        admin_notifications_enabled: settings.admin_notifications_enabled,
        default_max_org_admins: Number(settings.default_max_org_admins),
        default_max_teams: Number(settings.default_max_teams),
        default_max_users_per_team: Number(settings.default_max_users_per_team),
        default_max_nova_billing_users: Number(settings.default_max_nova_billing_users),
        feature_flags: settings.feature_flags || {},
        inactivity_timeout_minutes: Number(settings.inactivity_timeout_minutes),
      };
      const { data } = await axios.patch(`${API}/admin/platform-settings`, payload, { withCredentials: true });
      setSettings(data.settings);
      setDemoRecipients((data.settings.demo_request_notifications?.recipients || []).join(", "));
      setQuoteRecipients((data.settings.quote_accepted_notifications?.recipients || []).join(", "));
      setMessage("Platform settings saved.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-[#111C33] dark:text-slate-400" data-testid="settings-loading">Loading platform settings…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">Only safe platform-wide settings are shown. Infrastructure secrets and credentials are never exposed here.</p>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="settings-save-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Save settings
        </button>
      </div>
      {message && <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-400" data-testid="settings-success">{message}</p>}
      {error && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400" data-testid="settings-error">{error}</p>}

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="space-y-6">
          <Panel title="Platform Information" subtitle="Platform display defaults" testId="settings-general">
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Platform name</label>
            <input
              value={settings.platform_name || ""}
              onChange={(e) => update("platform_name", e.target.value)}
              data-testid="settings-platform-name"
              className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
            />
          </Panel>

          <Panel title="Platform Email Addresses" subtitle="Single source of truth for every functional outbound email address" testId="settings-platform-emails">
            <div className="space-y-4">
              {PLATFORM_EMAIL_FIELDS.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">{field.label}</label>
                  <input
                    type="email"
                    value={settings.platform_emails?.[field.key] || ""}
                    onChange={(e) => updateEmail(field.key, e.target.value)}
                    data-testid={`settings-email-${field.key.replace(/_/g, "-")}`}
                    className={`mt-2 w-full rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:bg-white/5 dark:text-white ${emailErrors[field.key] ? "border-red-400 focus:border-red-500 dark:border-red-500/60" : "border-slate-300 focus:border-blue-600 dark:border-white/15"}`}
                  />
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{field.hint}</p>
                  {emailErrors[field.key] && (
                    <p className="mt-1 text-xs font-semibold text-red-600 dark:text-red-400" data-testid={`settings-email-${field.key.replace(/_/g, "-")}-error`}>
                      Enter a valid, non-blank email address.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Email Notifications" subtitle="Independent notification controls per event" testId="settings-email-notifications">
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-slate-900 dark:text-white">Demo request notifications</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Email these recipients when a demo request arrives.</p>
                  </div>
                  <Toggle
                    checked={settings.demo_request_notifications?.enabled || false}
                    onChange={(v) => update("demo_request_notifications", { ...settings.demo_request_notifications, enabled: v })}
                    testId="settings-demo-notifications-toggle"
                  />
                </div>
                <textarea
                  value={demoRecipients}
                  onChange={(e) => setDemoRecipients(e.target.value)}
                  data-testid="settings-demo-notification-recipients"
                  placeholder="admin@novaopsis.com, sales@novaopsis.com"
                  className="mt-3 min-h-20 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
                />
              </div>

              <div className="border-t border-slate-200 pt-6 dark:border-white/10">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-slate-900 dark:text-white">Quote accepted notifications</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Email these recipients when a customer accepts a quote.</p>
                  </div>
                  <Toggle
                    checked={settings.quote_accepted_notifications?.enabled || false}
                    onChange={(v) => update("quote_accepted_notifications", { ...settings.quote_accepted_notifications, enabled: v })}
                    testId="settings-quote-notifications-toggle"
                  />
                </div>
                <textarea
                  value={quoteRecipients}
                  onChange={(e) => setQuoteRecipients(e.target.value)}
                  data-testid="settings-quote-notification-recipients"
                  placeholder="admin@novaopsis.com, sales@novaopsis.com"
                  className="mt-3 min-h-20 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
                />
              </div>

              <div className="border-t border-slate-200 pt-6 dark:border-white/10">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-slate-900 dark:text-white">Admin notifications</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Allow platform-level administrative notifications.</p>
                  </div>
                  <Toggle checked={settings.admin_notifications_enabled} onChange={(v) => update("admin_notifications_enabled", v)} testId="settings-admin-notifications-toggle" />
                </div>
              </div>
            </div>
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel title="Authentication" subtitle="Supported sign-in and invitation behavior" testId="settings-authentication">
            <div className="space-y-4 text-sm">
              <div className="flex items-center justify-between text-slate-700 dark:text-slate-300"><span>Email/password</span><span className="font-bold text-emerald-700 dark:text-emerald-400">Enabled</span></div>
              <div className="flex items-center justify-between text-slate-700 dark:text-slate-300"><span>Google sign-in</span><span className="font-bold text-emerald-700 dark:text-emerald-400">Enabled</span></div>
              <label className="block pt-2 font-semibold text-slate-700 dark:text-slate-300">Invitation expiration (days)</label>
              <input
                type="number"
                min="1"
                max="30"
                value={settings.invitation_expiration_days}
                onChange={(e) => update("invitation_expiration_days", Number(e.target.value))}
                data-testid="settings-invitation-days"
                className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
              />
            </div>
          </Panel>

          <Panel title="Session Security" subtitle="Default sliding inactivity logout window for every organization" testId="settings-session-security">
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Inactivity timeout (minutes)</label>
            <input
              type="number"
              min="30"
              max="100"
              value={settings.inactivity_timeout_minutes ?? 30}
              onChange={(e) => update("inactivity_timeout_minutes", e.target.value === "" ? "" : Number(e.target.value))}
              data-testid="settings-inactivity-timeout-minutes"
              className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Must be between 30 and 100 minutes. Organizations can override this individually in their own Settings.</p>
          </Panel>

          <Panel title="Organizations" subtitle="Defaults for future onboarding" testId="settings-organizations">
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Default organization status</label>
            <select
              value={settings.default_organization_status}
              onChange={(e) => update("default_organization_status", e.target.value)}
              data-testid="settings-default-org-status"
              className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
            >
              {ORG_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
          </Panel>

          <Panel title="Solutions" subtitle="Global availability defaults" testId="settings-solutions">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-slate-900 dark:text-white">Solutions globally available</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Organization-level enablement can be layered on later.</p>
              </div>
              <Toggle checked={settings.solutions_globally_enabled} onChange={(v) => update("solutions_globally_enabled", v)} testId="settings-solutions-toggle" />
            </div>
          </Panel>

          <Panel title="Organization Access Limits" subtitle="Defaults applied to every new organization (each org can still be overridden individually)" testId="settings-org-limits">
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">Org Owners</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Fixed — exactly one owner per organization, cannot be changed.</p>
                </div>
                <span className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-bold text-slate-500 dark:bg-white/10 dark:text-slate-400" data-testid="settings-max-org-owners">1</span>
              </div>
              <div className="border-t border-slate-200 pt-4 dark:border-white/10">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Org Admins</label>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  value={settings.default_max_org_admins ?? ""}
                  onChange={(e) => update("default_max_org_admins", e.target.value === "" ? "" : Number(e.target.value))}
                  data-testid="settings-max-org-admins"
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
                />
              </div>
              <div className="border-t border-slate-200 pt-4 dark:border-white/10">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Teams</label>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  value={settings.default_max_teams ?? ""}
                  onChange={(e) => update("default_max_teams", e.target.value === "" ? "" : Number(e.target.value))}
                  data-testid="settings-max-teams"
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
                />
              </div>
              <div className="border-t border-slate-200 pt-4 dark:border-white/10">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Users per Team</label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={settings.default_max_users_per_team ?? ""}
                  onChange={(e) => update("default_max_users_per_team", e.target.value === "" ? "" : Number(e.target.value))}
                  data-testid="settings-max-users-per-team"
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
                />
              </div>
              <div className="border-t border-slate-200 pt-4 dark:border-white/10">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Nova Billing Users</label>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  value={settings.default_max_nova_billing_users ?? ""}
                  onChange={(e) => update("default_max_nova_billing_users", e.target.value === "" ? "" : Number(e.target.value))}
                  data-testid="settings-max-nova-billing-users"
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
                />
              </div>
            </div>
          </Panel>

          <Panel title="Feature Management" subtitle="Future staged rollout controls" testId="settings-feature-management">
            {Object.keys(settings.feature_flags || {}).length === 0 ? (
              <EmptyState icon={Settings} title="No feature flags configured." message="Future platform feature flags will appear here without exposing infrastructure configuration." testId="feature-flags-empty" />
            ) : (
              <div className="text-slate-700 dark:text-slate-300">{Object.entries(settings.feature_flags).map(([key, value]) => <p key={key}>{key}: {String(value)}</p>)}</div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
